// ─────────────────────────────────────────────────────────────
// Measurement Protocol — CREATE PAGE ("Initiate New MP" panel)
//
// Extracted out of the original monolithic MPCreationIndex.js into its
// own self-contained component. It owns all of its own form state and
// data fetching (Facility/Lab list → Test Type list → Project list),
// and only tells the parent index page about the outcome via
// `onCreated({ testTypeID, facilityID })` once a new MP has actually
// been initiated — the parent is then responsible for refreshing the
// Test Overview list and expanding the right row.
//
// Styling & validation are now aligned with the other form pages:
//  • labels use the shared <Lbl /> (slate-500 uppercase, red-500 *)
//  • dropdowns use the shared <PortalDropdown /> at the standard field
//    height (h-11, slate-50 bg, slate-200 border, primary focus ring)
//  • required fields show INLINE red-border + message validation (via
//    the PortalDropdown `error` prop) instead of a toast only
//  • the field row is a responsive grid so it reflows on small screens
// ─────────────────────────────────────────────────────────────
import { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";

import { toArray, BRAND, Lbl, PortalDropdown } from "./shared/mpShared";
import {
  fetchSetupUtilLabList,
  fetchSetupUtilTestDetails,
} from "../../API_Service/LabBooking_Slice";
import {
  MP_GetProjectList,
  MP_InitiateMP,
} from "../../API_Service/MeasurementProtocol_Slice";
import { companyID, getEmployeeID } from "../../Common/UIComponents";
import { useGlobalLoader } from "../../Common/GlobalLoader";

function MPCreatePage({ open, onClose, onCreated, notify }) {
  const dispatch = useDispatch();
  const { showLoader, hideLoader } = useGlobalLoader();

  const [selectedLab, setSelectedLab] = useState("");
  const [isLabLoading, setIsLabLoading] = useState(false);
  const [labData, setLabData] = useState([]);

  const [testTypeOptions, setTestTypeOptions] = useState([]);
  const [selectedTestType, setSelectedTestType] = useState("");
  const [testTypeLoading, setTestTypeLoading] = useState(false);

  const [selectedProject, setSelectedProject] = useState("");
  const [isProjectLoading, setIsProjectLoading] = useState(false);
  const [projectData, setProjectData] = useState([]);

  const [creating, setCreating] = useState(false);

  // Inline validation — mirrors the { field: message } pattern used on
  // the other create/edit pages.
  const [errors, setErrors] = useState({});

  const fetchLabList = useCallback(async () => {
    setIsLabLoading(true);
    try {
      const res = await dispatch(
        fetchSetupUtilLabList({ companyID, employeeID: getEmployeeID() }),
      );
      const data = toArray(res?.payload?.data?.allLabs);
      setLabData(
        data.map((l) => ({
          value: l.facilityId,
          label: l.facilityName,
        })),
      );
    } catch (err) {
      console.error("Error fetching lab list:", err);
      notify?.("Couldn't load the facility list.", "error");
    } finally {
      setIsLabLoading(false);
    }
  }, [dispatch, notify]);

  // Load the Facility/Lab list once, as soon as the panel first opens
  // (not before, so the list index page doesn't pay this cost until
  // the user actually wants to create an MP).
  useEffect(() => {
    if (open && labData.length === 0 && !isLabLoading) {
      fetchLabList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!selectedLab) return;
    let cancelled = false;
    setTestTypeLoading(true);
    (async () => {
      try {
        const res = await dispatch(
          fetchSetupUtilTestDetails({
            facilityID: selectedLab,
            companyID,
            projectID: 0,
          }),
        );
        if (cancelled) return;
        const data = toArray(res?.payload?.data?.testType);
        setTestTypeOptions(
          data.map((t) => ({
            value: t.testTypeID,
            label: t.testTypeName,
          })),
        );
      } catch (err) {
        console.error("Error fetching test type details:", err);
        notify?.("Couldn't load test types for this facility.", "error");
      } finally {
        if (!cancelled) setTestTypeLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLab, dispatch]);

  useEffect(() => {
    if (!selectedTestType) return;
    let cancelled = false;
    setIsProjectLoading(true);
    (async () => {
      try {
        const res = await dispatch(
          MP_GetProjectList({ companyID, selectedTestType }),
        );
        if (cancelled) return;
        const data = toArray(res?.payload?.data);
        setProjectData(
          data.map((l) => ({
            value: l.Project_Id,
            label: l.Project_Name,
          })),
        );
      } catch (err) {
        console.error("Error fetching project list:", err);
        notify?.("Couldn't load the project list.", "error");
      } finally {
        if (!cancelled) setIsProjectLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch, selectedTestType, notify]);

  const resetForm = () => {
    setSelectedLab("");
    setSelectedTestType("");
    setSelectedProject("");
    setTestTypeOptions([]);
    setProjectData([]);
    setErrors({});
  };

  const handleLabChange = (labId) => {
    setSelectedLab(labId);
    setSelectedTestType("");
    setSelectedProject("");
    setTestTypeOptions([]);
    setErrors((prev) => ({ ...prev, facility: "", testType: "" }));
    if (!labId) return;
    setTestTypeLoading(true);
  };

  const handleTestTypeChange = (v) => {
    setSelectedTestType(v);
    setErrors((prev) => ({ ...prev, testType: "" }));
  };

  const validate = () => {
    const errs = {};
    if (!selectedLab) errs.facility = "Facility / Lab is required.";
    if (!selectedTestType) errs.testType = "Test Type is required.";
    return errs;
  };

  const handleStartCreation = async () => {
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setCreating(true);
    // Global overlay so the whole page visibly shows "working…" while
    // the new MP is being initiated, instead of only the button
    // looking disabled — and so the rest of the page can't be
    // interacted with mid-request.
    showLoader();
    try {
      const payload = {
        companyID,
        facilityID: selectedLab,
        testTypeID: selectedTestType,
        projectID: selectedProject || 0,
        createdBy: getEmployeeID(),
      };
      await dispatch(MP_InitiateMP(payload));
      notify?.("New Measurement Protocol initiated.");
      onCreated?.({
        testTypeID: selectedTestType,
        facilityID: selectedLab,
      });
    } catch (err) {
      console.error("Error initiating new MP:", err);
      notify?.("Couldn't initiate a new MP. Please try again.", "error");
    } finally {
      setCreating(false);
      hideLoader();
      onClose?.();
      resetForm();
    }
  };

  if (!open) return null;

  return (
    <div
      className="bg-white rounded-2xl border border-[#e5e9e9] shadow-sm w-full max-w-full min-w-0"
      style={{ animation: "fadeUp .2s ease" }}
    >
      {/* Header — neutral, matched to the other pages' section headers */}
      <div className="flex items-center gap-3 px-5 py-3 border-b border-[#ebeeee] bg-[#f7fafa]">
        <div className="h-9 w-9 rounded-full bg-white border border-[#e5e9e9] shadow-sm flex items-center justify-center text-[#08757C] flex-shrink-0">
          <span className="material-symbols-outlined text-[20px]">
            rocket_launch
          </span>
        </div>
        <div>
          <h3 className="text-sm font-bold text-[#181c1d]">
            Initiate New Measurement Protocol
          </h3>
          <p className="text-[11px] text-[#6e797a]">
            Select Facility and Test Type (required) · Project Name is optional
          </p>
        </div>
        <button
          onClick={() => {
            onClose?.();
            resetForm();
          }}
          className="ml-auto h-7 w-7 flex items-center justify-center rounded-lg text-[#6e797a] hover:bg-[#f1f4f4] transition-all"
        >
          <span className="material-symbols-outlined text-[18px]">close</span>
        </button>
      </div>

      {/* Fields — responsive grid so they reflow on small screens */}
      <div className="px-5 py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="min-w-0">
            <Lbl required>Facility / Lab</Lbl>
            <PortalDropdown
              value={selectedLab}
              onChange={handleLabChange}
              options={labData}
              placeholder={isLabLoading ? "Loading…" : "Select Facility"}
              error={errors.facility}
            />
          </div>

          <div className="min-w-0">
            <Lbl required>Test Type</Lbl>
            <PortalDropdown
              value={selectedTestType}
              onChange={handleTestTypeChange}
              options={testTypeOptions}
              placeholder={
                testTypeLoading
                  ? "Loading…"
                  : !selectedLab
                    ? "Select facility first"
                    : "Select Test Type"
              }
              disabled={!selectedLab || testTypeLoading}
              error={errors.testType}
            />
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">
                Project Name
              </label>
              <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-400 uppercase tracking-wide">
                Optional
              </span>
            </div>
            <PortalDropdown
              value={selectedProject}
              onChange={(v) => setSelectedProject(v)}
              options={projectData}
              placeholder={
                isProjectLoading ? "Loading…" : "Select Project (optional)"
              }
            />
          </div>
        </div>

        {/* Selected summary chips + action */}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
          {selectedLab && selectedTestType ? (
            <div className="flex flex-col gap-1.5 min-w-0">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                Creating for
              </p>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-teal-50 text-[#08757C] text-[11px] font-bold rounded-lg border border-teal-200">
                  <span className="material-symbols-outlined text-[12px]">
                    science
                  </span>
                  {labData.find((l) => l.value === selectedLab)?.label}
                </span>
                <span className="text-[#c0cbcc] text-xs">·</span>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-teal-50 text-[#08757C] text-[11px] font-bold rounded-lg border border-teal-200">
                  <span className="material-symbols-outlined text-[12px]">
                    biotech
                  </span>
                  {
                    testTypeOptions.find((t) => t.value === selectedTestType)
                      ?.label
                  }
                </span>
                {selectedProject && (
                  <>
                    <span className="text-[#c0cbcc] text-xs">·</span>
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-violet-50 text-violet-700 text-[11px] font-bold rounded-lg border border-violet-200">
                      <span className="material-symbols-outlined text-[12px]">
                        folder
                      </span>
                      {projectData
                        .find((p) => p.value === selectedProject)
                        ?.label?.split("·")[0]
                        ?.trim()}
                    </span>
                  </>
                )}
              </div>
            </div>
          ) : (
            <span />
          )}

          <button
            onClick={handleStartCreation}
            disabled={creating}
            className="h-11 px-6 text-white text-[13px] font-bold rounded-lg hover:brightness-110 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 flex-shrink-0"
            style={{ background: BRAND }}
          >
            {creating ? "Starting…" : "Start Creation"}
            <span className="material-symbols-outlined text-[16px]">
              {creating ? "progress_activity" : "arrow_forward"}
            </span>
          </button>
        </div>
      </div>

      <div className="px-5 py-2.5 border-t border-[#ebeeee] flex items-center gap-2 bg-[#fafcfc]">
        <span className="material-symbols-outlined text-[14px] text-slate-400">
          info
        </span>
        <p className="text-[11px] text-slate-400">
          <strong className="text-[#6e797a]">Facility</strong> and{" "}
          <strong className="text-[#6e797a]">Test Type</strong> are required to
          create an MP.
          <strong className="text-[#6e797a]"> Project Name</strong> is optional
          — leave blank for internal or ad-hoc testing.
        </p>
      </div>
    </div>
  );
}

export default MPCreatePage;
export { MPCreatePage };
